import { TableComponent, TableLoader } from 'components';
import { FC, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useCurrentUser } from '_graphql/cache/auth';
import numeral from 'numeral';
import { useWalletTransactions } from '../hooks/use-wallet';
import { GetTransactions_getTransactions } from '_graphql/queries/__generated__/GetTransactions';
import TransactionDetailDrawer from '../components/transaction-detail-drawer';
import {
  getMetadataDescription,
  getOrderItemIdFromMetadata,
  parseTransactionMetadata,
} from '../utils/transaction-metadata';

type Props = {};

const statusStyles: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

const TransactionStatusBadge: FC<{ status?: string | null }> = ({ status }) => {
  const normalized = status?.toLowerCase() ?? '';
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
        statusStyles[normalized] ?? 'bg-gray-100 text-gray-800'
      }`}
    >
      {status ?? 'N/A'}
    </span>
  );
};

const TransactionsTab: FC<Props> = () => {
  const currentUser = useCurrentUser();
  const [selectedTransaction, setSelectedTransaction] =
    useState<GetTransactions_getTransactions | null>(null);
  const { transactions, loading, refetch } = useWalletTransactions({
    userId: currentUser?.id,
  });

  return (
    <>
      <TableComponent
        title={'Transactions'}
        columns={[
          {
            accessor: 'reference',
            name: 'Reference',
            render: (value: GetTransactions_getTransactions) => (
              <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-900">
                  {value?.reference || 'N/A'}
                </p>
                <p className="text-xs text-gray-500">
                  {moment(value?.created_at).format('Do MMM, YYYY hh:mm A')}
                </p>
              </div>
            ),
          },
          {
            accessor: 'type',
            name: 'Type',
            render: (value: GetTransactions_getTransactions) =>
              _.startCase(value?.type) || 'N/A',
          },
          {
            accessor: 'direction',
            name: 'Direction',
            render: (value: GetTransactions_getTransactions) => (
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  value?.direction === 'inbound'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {_.startCase(value?.direction)}
              </span>
            ),
          },
          {
            accessor: 'amount',
            name: 'Amount',
            render: (value: GetTransactions_getTransactions) => (
              <span className="font-medium text-gray-900">
                {value?.currency?.code}{' '}
                {numeral(value?.amount).format('0,0.00')}
              </span>
            ),
          },
          {
            accessor: 'metadata',
            name: 'Description',
            render: (value: GetTransactions_getTransactions) => {
              const metadata = parseTransactionMetadata(value?.metadata);
              const description = getMetadataDescription(metadata) ?? '—';
              return (
                <span className="line-clamp-2 text-sm text-gray-600">
                  {description}
                </span>
              );
            },
          },
          {
            accessor: 'status',
            name: 'Status',
            render: (value: GetTransactions_getTransactions) => (
              <TransactionStatusBadge status={value?.status} />
            ),
          },
        ]}
        hasSearch
        data={transactions}
        loading={loading}
        refetch={refetch}
        onRowClick={setSelectedTransaction}
        renderLoader={() => <TableLoader items={[1, 1, 1, 1, 1, 1]} />}
        emptyDescription="No transactions found"
      />

      <TransactionDetailDrawer
        open={Boolean(selectedTransaction)}
        setOpen={(open) => {
          if (!open) {
            setSelectedTransaction(null);
          }
        }}
        transaction={selectedTransaction}
        orderItemId={getOrderItemIdFromMetadata(
          parseTransactionMetadata(selectedTransaction?.metadata)
        )}
      />
    </>
  );
};

export default TransactionsTab;
